// gamesData.js (Oct 5, 2026)
// The games TTO offers, written once. Read by GamesPage.js (the public /games page)
// and by the fact-sheet build (Docs/Marketing/Games_Fact_Sheet/build.js →
// frontend/public/downloads/TTO_Games.pdf), so the page and the PDF always agree.
//
// List only what exists. When a game ships, add it here (isNew: true for its first
// weeks), rebuild the PDF, and add its guide slug so the page links to it.

export const GAMES_UPDATED = 'October 2026';

export const GAMES_INTRO =
  'Play the games your group already plays. TTO sets them up, keeps score live, does the handicap math and works out who pays whom.';

export const HOW_IT_WORKS = [
  {
    title: 'Set up',
    text: 'The organizer picks the games for the round, as many as he likes, and sets a wager for each, or $0 to just name the winners. Players opt in from their app, or the organizer puts them in. Every game is optional.',
  },
  {
    title: 'Score',
    text: 'Players enter scores hole by hole on their phones, and one player can keep the card for up to three others. Games and the live leaderboard update as each score goes in. Side bets (sandies, polies, closest to the pin, presses) are a tap on the hole screen.',
  },
  {
    title: 'Settle',
    text: 'The round finalizes itself once every card is in. Every player gets a statement showing who pays whom, netted across all the games, and the organizer can email everyone the results with one click.',
  },
];

export const LIVE_SCORING_NOTE = {
  lead: 'Games require TTO live scoring.',
  text: 'Every player in a game needs his scores entered in the app as he plays, either on his own phone or by someone in his group keeping his card (one phone can score the whole foursome). That is what scores the games, settles the money and builds each player’s stats.',
};

export const HOLE_SCREEN_CAPTION = 'The hole screen: big buttons, the round at a glance, and Next always under the thumb.';

export const BUILT_IN = [
  {
    title: 'Course handicaps, automatic',
    text: 'Each player’s handicap index comes from GHIN, and TTO works out his course handicap from the tee he plays. Players on different tees are each handled correctly.',
  },
  {
    title: 'Strokes your way',
    text: 'Net games give strokes on the hardest holes, either **off the low man** (the best player plays off scratch, the rest get the difference) or **full course handicap**. Or play gross.',
  },
  {
    title: 'Individual or team',
    text: 'Many games play either way. Nassau can be every man for himself or 2 vs 2 best ball, Sixes rotates partners inside the foursome, and Dots settles within each group or across the whole field. Team games use your pairings as the teams.',
  },
  {
    title: 'An AI recap of the round',
    text: 'When the round is done, TTO writes a short story of the day: the headline, the highlights and a "Your round" box for each player, ready to share. Every fact is checked against the scores.',
  },
  {
    title: 'Player stats, every round',
    text: 'Each live-scored round adds to every player’s stats: scoring average and trend, putts and 3-putts, greens in regulation, scoring by par, his toughest holes, and head to head against the group.',
  },
  {
    title: 'Post to GHIN',
    text: 'Rounds post hole by hole to GHIN straight from the app, so nobody re-enters a scorecard at home.',
  },
];

export const EVENTS_NOTE = {
  lead: 'Built for events, too.',
  text: 'Competitions come with printed scorecards and cart signs (a QR code lets players score without signing in), a TV leaderboard for the clubhouse, tie-breaking, one-click finalize and an AI recap of the event.',
};

// Which plans include games (Oct 5, 2026 — Dennis: games come with Plus and above,
// switched on by the plan). Shown on /games, the landing page and the fact sheet.
export const GAMES_PLAN = 'Included with Plus, Professional and League';

export const TURN_ON_NOTE = {
  lead: 'Games come with the Plus plan and above.',
  text: 'Plus ($9.99 a month), Professional and League include every game, switched on for your group automatically. On Free or Starter? Upgrade any time under Settings → Subscription & Billing. Step-by-step setup guides are in the app under 📖 Guides.',
};

// Each game: the table row (playAs / handicaps / money) and its card (intro, bullets,
// example). guide: the /guides slug, when there is one.
export const GROUP_GAMES = [
  {
    name: 'Skins', tableName: 'Skins (6 versions)',
    playAs: 'Individual', handicaps: 'Net (low man or full) or gross', money: 'Per skin, or a pot split by skins won',
    intro: 'The lowest score on a hole wins the skin. Three ways to pay, each net or gross:',
    bullets: [
      '**Carryover:** a tie carries the skin to the next hole, which is then worth double, and it keeps building.',
      '**No carryover:** a tied hole is simply dropped.',
      '**Pot:** everyone buys in; the pot is split by skins won ($30, 7 skins: $4.29 each).',
    ],
    guide: 'how-to-set-up-a-skins-game',
  },
  {
    name: 'Nassau', isNew: true,
    playAs: 'Individual or 2 vs 2', handicaps: 'Net (low man or full) or gross', money: 'Per bet: front, back, overall, presses',
    intro: 'Three match-play bets: the front 9, the back 9 and the overall 18. Each hole is won by the lower score or halved; the side that wins more holes wins the bet. All square pays nothing. 18-hole rounds only.',
    bullets: [
      '**Matches:** every player against every other in his group, or the first two names on the pairing against the last two.',
      '**Presses:** 2 down on a nine starts a new bet to the end of that nine. Automatic, asked (a **Press?** prompt on the hole screen), or none.',
    ],
    example: '$5 Nassau: win the front, halve the back, win the overall. You collect $10.',
    guide: 'how-to-set-up-a-nassau-golf-game',
  },
  {
    name: 'Match Play', isNew: true,
    playAs: 'Singles or 2 vs 2 four-ball', handicaps: 'Net (USGA or full) or gross', money: 'Per match',
    intro: 'Win more holes than the other side; **“3 & 2”** when the lead passes the holes left. One bet a match, 18 or a nine. Singles in each group, or the first two on the pairing against the last two.',
    guide: 'how-to-set-up-a-match-play-golf-game',
  },
  {
    name: 'Sixes', isNew: true,
    playAs: 'Foursomes, 2 vs 2, partners rotate', handicaps: 'Net (low man or full) or gross', money: 'Per match, presses optional',
    intro: 'Three six-hole matches inside each foursome, two against two, and everyone partners everyone once. Partners come from the carts:',
    bullets: [
      '**Holes 1–6** cart against cart; **7–12** each driver with the other cart’s passenger; **13–18** drivers against passengers.',
      'Players set the carts on the first tee. Best ball, match play; a tied match is a push. Presses automatic, asked or none.',
    ],
    example: '$5 a match: win the first two, push the third. You collect $10.',
    guide: 'how-to-set-up-a-sixes-golf-game',
  },
  {
    name: 'Dots', isNew: true,
    playAs: 'Within each group or the whole field', handicaps: 'Gross birdies, tapped sandies and polies', money: 'Per dot, difference between each pair',
    intro: 'A dot for every birdie, sandy and poley; the organizer picks which count. Birdies count by themselves (birdie 1, eagle 2). Players tap **Sandy** or **Poley** on the hole. Each player settles the difference in dots with his group, or the whole field.',
    example: '$1 a dot: Al 4 dots, Bob 1. Bob pays Al $3.',
    guide: 'how-to-set-up-a-dots-golf-game',
  },
  {
    name: 'Putts Differential',
    playAs: 'Individual', handicaps: 'None', money: 'Per putt, difference between each pair',
    intro: 'Each player records his putts. Each pair settles the difference in total putts × the wager.',
    example: 'Mark 28, Sam 30, Kirk 32 at $0.50 a putt: Mark collects $1 from Sam and $2 from Kirk.',
    guide: 'how-to-set-up-a-putts-golf-game',
  },
  {
    name: 'Closest to the Pin',
    playAs: 'Individual', handicaps: 'None', money: 'Prize per par 3, or so much a player',
    intro: 'On each par 3 (or holes you choose), the closest player taps **“On the green and closer?”** and a closer player takes it over. Optional **must make par**. A set prize per hole, or so much a player per hole. Works on team rounds too.',
    guide: 'how-to-set-up-closest-to-the-pin',
  },
];

export const COMPETITIONS = [
  {
    name: 'Stroke Play',
    playAs: 'Individual', handicaps: 'Net, gross or both (95% allowance)', money: 'Buy-in and places, or handled outside TTO',
    intro: 'Everyone plays his own ball; the lowest total wins, on net, gross or both. Winner take all, paid places (50 / 30 / 20), or money handled outside TTO. Front, back and 18 can pay separately; ties by USGA scorecard playoff.',
    guide: 'how-to-set-up-a-stroke-play-game',
  },
  {
    name: 'Stableford', isNew: true,
    playAs: 'Individual: within each group, or the whole field', handicaps: 'Net (95% allowance) or gross', money: 'A pot paid by places (front, back and overall too), or per point',
    intro: 'Points on every hole, and the most points wins: double bogey or worse 0, bogey 1, par 2, birdie 3, eagle 4, albatross 5, on net scores. A blow-up hole costs no more than that hole.',
    bullets: [
      '**Within each group:** each foursome, threesome or twosome on the Pairings page is its own Stableford, so uneven groups and late cancels are no problem. Or play the **whole field** as one.',
      '**Money:** a pot by places (the whole round, or front, back and overall), or so much a point.',
    ],
    example: '$5 buy-in, four players, winner takes all: 36 points takes the $20 pot.',
    guide: 'how-to-set-up-a-stableford-golf-game',
  },
  {
    name: 'Best Ball',
    playAs: 'Teams of 2, 3 or 4', handicaps: 'USGA allowance per player', money: 'Buy-in and places; teammates split',
    intro: 'Everyone plays his own ball; the team counts its best score on each hole (3-man: best 1 or 2; 4-man: best 1, 2 or 3, or **one gross + one net** from two different players), with each player’s own USGA allowance. A **blind draw** fills a short foursome.',
    guide: 'how-to-set-up-a-best-ball-game',
  },
  {
    name: 'Shamble',
    playAs: 'Teams of 2 or 4', handicaps: 'USGA selected-drive allowance', money: 'Buy-in and places; teammates split',
    intro: 'The team picks the best drive, then each player plays his own ball in, counted like Best Ball, with the USGA selected-drive allowance. Doesn’t post to GHIN.',
    guide: 'how-to-set-up-a-shamble-golf-tournament',
  },
  {
    name: 'Captain’s Choice', cardName: 'Captain’s Choice (Scramble)',
    playAs: 'Teams of 2, 3 or 4', handicaps: 'Gross, or net with a team handicap', money: 'Results only: you pay the places',
    intro: 'Everyone hits, the team picks the best shot and all play from there: one team score per hole. Net uses a USGA team handicap. TTO names the places; you pay them. Doesn’t post to GHIN.',
    guide: 'how-to-set-up-a-captains-choice-golf-tournament',
  },
];

export const GAME_COUNT = GROUP_GAMES.length + COMPETITIONS.length;

// "**bold**" → [{ text, bold }] pieces, for both renderers.
export const boldParts = (s) => String(s).split(/(\*\*[^*]+\*\*)/).filter(Boolean).map(p => (
  p.startsWith('**') && p.endsWith('**') ? { text: p.slice(2, -2), bold: true } : { text: p, bold: false }
));
